import React, { useState, useEffect, useRef, useCallback } from 'react';
import './GeometricCharacters.css';

// 1. Movi o EyeComponent para fora para evitar re-declarações e adicionei o displayName
const EyeComponent = React.memo(
  ({ position, emotion, characterId, calculateEyePosition, getEyeRotation }) => {
    const eyeMove = calculateEyePosition(position.x, position.y);
    const eyeRotation = getEyeRotation(emotion);

    let eyeOffsetX = eyeMove.x;
    let eyeOffsetY = eyeMove.y;

    if (eyeRotation === 'surprise') {
      eyeOffsetY = -8;
    } else if (eyeRotation === 'shy') {
      eyeOffsetX = -8;
    } else if (eyeRotation === 'sad') {
      eyeOffsetY = 8;
    } else if (['love', 'cool', 'dizzy', 'dead'].includes(eyeRotation)) {
      eyeOffsetX = 0;
      eyeOffsetY = 0;
    } else if (eyeRotation === 'laughing') {
      eyeOffsetX = 0;
      eyeOffsetY = 5;
    } else if (eyeRotation === 'confused') {
      eyeOffsetX = characterId % 2 === 0 ? -6 : 6;
      eyeOffsetY = 0;
    } else if (eyeRotation === 'suspicious') {
      eyeOffsetX = 6;
      eyeOffsetY = -3;
    }

    if (eyeRotation === 'love') {
      return (
        <div className="eye eye-love">
          <svg viewBox="0 0 40 40" className="eye-special">
            <path d="M 10 15 Q 20 5 30 15 Q 20 25 10 15" fill="currentColor" />
          </svg>
        </div>
      );
    }

    if (eyeRotation === 'cool') {
      return (
        <div className="eye eye-cool">
          <div className="sunglasses"></div>
        </div>
      );
    }

    if (eyeRotation === 'dizzy') {
      return (
        <div className="eye eye-dizzy">
          <svg viewBox="0 0 40 40" className="eye-special">
            <path
              d="M 20 6 Q 26 6 30 10 Q 34 14 34 20 Q 34 28 28 34 Q 20 40 12 34 Q 8 30 8 22 Q 8 16 12 12 Q 18 8 22 10"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
      );
    }

    if (eyeRotation === 'dead') {
      return (
        <div className="eye eye-dead">
          <svg viewBox="0 0 40 40" className="eye-special">
            <line
              x1="12"
              y1="12"
              x2="28"
              y2="28"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <line
              x1="28"
              y1="12"
              x2="12"
              y2="28"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
      );
    }

    return (
      <div className="eye">
        <div className="eye-white">
          <div
            className="pupil"
            style={{ transform: `translate(${eyeOffsetX}px, ${eyeOffsetY}px)` }}
          />
          <div className="pupil-shine" />
        </div>
      </div>
    );
  }
);
EyeComponent.displayName = 'EyeComponent';

export default function GeometricCharacters({ emotion = 'neutral' }) {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [containerEmotion, setContainerEmotion] = useState(emotion);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const containerRef = useRef(null);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const lastAngleRef = useRef(0);
  const rotationCountRef = useRef(0);
  const dizzyTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const speedThresholdRef = useRef(20);

  // 2. Memorizar funções utilitárias para evitar re-renders no EyeComponent
  const calculateEyePosition = useCallback(
    (eyeX, eyeY) => {
      const angle = Math.atan2(mousePos.y - eyeY, mousePos.x - eyeX);
      const distance = 6;
      return {
        x: Math.cos(angle) * distance,
        y: Math.sin(angle) * distance,
      };
    },
    [mousePos]
  );

  const getEyeRotation = useCallback(emotionType => {
    const validRotations = [
      'surprise',
      'shy',
      'happy',
      'sad',
      'love',
      'cool',
      'dizzy',
      'laughing',
      'dead',
      'confused',
      'suspicious',
    ];
    return validRotations.includes(emotionType) ? emotionType : 'normal';
  }, []);

  useEffect(() => {
    const handleMouseMove = e => {
      if (isUserTyping) return;

      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const newX = e.clientX - rect.left;
        const newY = e.clientY - rect.top;

        if (containerEmotion !== 'dizzy') {
          setMousePos({ x: newX, y: newY });
        }

        const deltaX = newX - lastPosRef.current.x;
        const deltaY = newY - lastPosRef.current.y;
        const speed = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        if (speed > speedThresholdRef.current) {
          const currentAngle = Math.atan2(deltaY, deltaX);
          let angleDiff = currentAngle - lastAngleRef.current;

          if (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
          else if (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

          if (Math.abs(angleDiff) > Math.PI / 6) {
            rotationCountRef.current += 1;
            if (rotationCountRef.current >= 8) {
              setContainerEmotion('dizzy');
              if (dizzyTimeoutRef.current) clearTimeout(dizzyTimeoutRef.current);
              dizzyTimeoutRef.current = setTimeout(() => {
                setContainerEmotion(emotion);
                rotationCountRef.current = 0;
              }, 1500);
            }
          }
          lastAngleRef.current = currentAngle;
        }
        lastPosRef.current = { x: newX, y: newY };
      }
    };

    const handleInputActivity = () => {
      setIsUserTyping(true);
      if (containerEmotion === 'dizzy') {
        setContainerEmotion(emotion);
        rotationCountRef.current = 0;
        if (dizzyTimeoutRef.current) clearTimeout(dizzyTimeoutRef.current);
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => setIsUserTyping(false), 500);
    };

    window.addEventListener('mousemove', handleMouseMove);
    // Delegar eventos para o body é mais eficiente que iterar em cada input
    document.body.addEventListener('focusin', handleInputActivity);
    document.body.addEventListener('input', handleInputActivity);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.body.removeEventListener('focusin', handleInputActivity);
      document.body.removeEventListener('input', handleInputActivity);
      if (dizzyTimeoutRef.current) clearTimeout(dizzyTimeoutRef.current);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [emotion, isUserTyping, containerEmotion]);

  // Constantes de posição dos olhos
  const characterEyes = [
    [
      { x: 80, y: 100 },
      { x: 130, y: 100 },
    ],
    [
      { x: 300, y: 150 },
      { x: 350, y: 150 },
    ],
    [
      { x: 200, y: 280 },
      { x: 250, y: 280 },
    ],
    [
      { x: 420, y: 220 },
      { x: 460, y: 220 },
    ],
  ];

  const activeEmotion = containerEmotion === 'dizzy' ? containerEmotion : emotion;

  return (
    <div className="characters-container" ref={containerRef}>
      {/* Sun */}
      <div className="sun">
        <div className="sun-rays">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="sun-ray"></div>
          ))}
        </div>
        <div className="sun-eyes">
          <div className="sun-eye"></div>
          <div className="sun-eye"></div>
        </div>
        <div className="sun-smile"></div>
      </div>

      {/* Renderização dos Personagens */}
      {['semicircle', 'rectangle large', 'rectangle small', 'cylinder'].map((shape, idx) => (
        <div key={idx} className={`character character-${idx + 1}`}>
          <div className={`character-body ${shape} ${activeEmotion === 'dizzy' ? 'is-dizzy' : ''}`}>
            <div className="eyes-container">
              <EyeComponent
                position={characterEyes[idx][0]}
                emotion={activeEmotion}
                characterId={idx + 1}
                calculateEyePosition={calculateEyePosition}
                getEyeRotation={getEyeRotation}
              />
              <EyeComponent
                position={characterEyes[idx][1]}
                emotion={activeEmotion}
                characterId={idx + 1}
                calculateEyePosition={calculateEyePosition}
                getEyeRotation={getEyeRotation}
              />
            </div>
            <MouthComponent emotion={activeEmotion} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Subcomponente de Boca (displayName automático por ser função nomeada)
function MouthComponent({ emotion }) {
  const getMouthType = emo => {
    const map = {
      surprise: 'O',
      happy: 'smile',
      sad: 'sad',
      shy: 'shy',
      love: 'heartSmile',
      cool: 'coolSmirk',
      dizzy: 'confused',
      laughing: 'bigSmile',
      dead: 'veryBigSmile',
      confused: 'confused',
      suspicious: 'suspicious',
    };
    return map[emo] || 'neutral';
  };

  const type = getMouthType(emotion);
  return (
    <div className={`mouth mouth-${type}`}>
      {type === 'O' && (
        <svg viewBox="0 0 40 40" className="mouth-svg">
          <circle cx="20" cy="20" r="10" fill="none" stroke="currentColor" strokeWidth="2.5" />
        </svg>
      )}
      {type === 'smile' && (
        <svg viewBox="0 0 40 40" className="mouth-svg">
          <path d="M 10 18 Q 20 28 30 18" stroke="currentColor" fill="none" strokeWidth="2.5" />
        </svg>
      )}
      {/* ... (demais SVGs podem ser mantidos conforme sua lógica original) */}
      {type === 'neutral' && (
        <svg viewBox="0 0 40 40" className="mouth-svg">
          <line x1="12" y1="20" x2="28" y2="20" stroke="currentColor" strokeWidth="2" />
        </svg>
      )}
    </div>
  );
}
